import React, { useState, useEffect, useCallback } from "react";
import {
  Key, ExternalLink, Loader, AlertCircle, ChevronDown, ChevronRight,
} from "lucide-react";
import StockApiTokenSection from "./StockApiTokenSection";
import { useTheme } from "../contexts/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL;

// Convierte un hex (#rrggbb) a rgba para fondos tintados. Si no es hex (p. ej.
// ya es un rgba del tema), lo devuelve tal cual.
const rgba = (hex, a) => {
  if (!hex || hex[0] !== "#") return hex;
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

/**
 * Sección "API Keys" del panel "Mi perfil" para clientes MATRIZ.
 *
 * Muestra, por cada clínica con token activo, una card colapsable con
 * `StockApiTokenSection` dentro para gestión completa (regenerar/revocar) más
 * el enlace a la documentación.
 *
 * - Acepta `clientes` (ya cargados por el padre). Si no se pasa, los carga ella
 *   misma desde `/api/mis-clientes-con-token` (que ya filtra por token enviado
 *   por el admin).
 * - NO se filtra por gestión de stock: el token también sirve la API de Datos
 *   (BI), independiente del inventario. Si el stock está inactivo se marca con
 *   el badge "SOLO DATOS" y el detalle indica qué endpoints quedan en 403.
 * - Si no hay ninguna, el componente no renderiza nada.
 * - Colores: sigue el tema (primario/secundario) del cliente.
 */
export default function ApiKeysMatrizSection({ clientes: clientesProp }) {
  const { theme } = useTheme();
  const [clientesState, setClientesState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});

  const usandoProp = Array.isArray(clientesProp);

  const cargar = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_URL}/api/mis-clientes-con-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Error al cargar tokens");
      setClientesState(data.clientes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!usandoProp) cargar(); }, [cargar, usandoProp]);

  // Fuente de datos: prop del padre si viene, si no el fetch interno. NO se filtra
  // por gestión de stock: el token también da acceso a la API de datos (BI), que es
  // independiente del inventario. El endpoint ya garantiza que tienen token.
  const clientes = (usandoProp ? clientesProp : clientesState);

  // Expandir la primera card por defecto (sin pisar lo que el usuario abra).
  useEffect(() => {
    if (clientes.length > 0) {
      setExpanded(prev => (Object.keys(prev).length ? prev : { [clientes[0].id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes.length]);

  const toggleExpanded = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Sin clínicas con token + stock → no mostramos la sección.
  if (!loading && !error && clientes.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Cabecera */}
      <div className="px-6 py-4 border-b" style={{ background: `linear-gradient(135deg, ${rgba(theme.primary, 0.06)}, ${rgba(theme.primary, 0.02)})` }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: rgba(theme.primary, 0.12) }}>
              <Key size={20} style={{ color: theme.primary }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">API Keys</h2>
              <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
                Gestiona los tokens de integración de tus clínicas. Como matriz puedes
                regenerar o revocar tokens — para emitir uno nuevo, contacta con CentralSalud.
              </p>
            </div>
          </div>
          <a
            href="/api-docs/datos"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            style={{ color: theme.primary }}
          >
            <ExternalLink size={13} /> Ver documentación
          </a>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
            <Loader size={16} className="animate-spin mr-2" /> Cargando tus API Keys...
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" /> {error}
          </div>
        )}

        {!loading && !error && clientes.length > 0 && (
          <div className="space-y-3">
            {clientes.map((c) => {
              const isOpen = !!expanded[c.id];
              const esMatriz = c.clinica_matriz === 0 || c.clinica_matriz === null;
              return (
                <div key={c.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                  {/* Card header colapsable */}
                  <button
                    onClick={() => toggleExpanded(c.id)}
                    className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
                    type="button"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen
                        ? <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                        : <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-800 truncate">{c.razon_social}</span>
                          {esMatriz && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: rgba(theme.secondary, 0.12), color: theme.secondary }}>
                              MATRIZ
                            </span>
                          )}
                          {c.alias && c.alias !== c.razon_social && (
                            <span className="text-xs text-gray-400">{c.alias}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {c.localidad && <span>{c.localidad}</span>}
                          <span>·</span>
                          <span>Token desde {c.stock_api_token_created_at ? new Date(c.stock_api_token_created_at).toLocaleDateString("es-ES") : "—"}</span>
                          {c.ultima_llamada && (
                            <>
                              <span>·</span>
                              <span>{c.total_llamadas} llamadas (última: {new Date(c.ultima_llamada).toLocaleDateString("es-ES")})</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {c.gestion_stock === "inactivo" && (
                        <span
                          className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"
                          title="Con el stock inactivo, los 7 endpoints de la API de Stock responden 403. La API de Datos (BI) funciona con normalidad."
                        >
                          SOLO DATOS
                        </span>
                      )}
                      <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                        ● ACTIVO
                      </span>
                    </div>
                  </button>

                  {/* Card body con StockApiTokenSection (reutilizamos para no duplicar lógica) */}
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-gray-50/40 p-4">
                      <StockApiTokenSection
                        clienteId={c.id}
                        modoStock={c.gestion_stock || "inactivo"}
                        docsHref="/api-docs/datos"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
